/**
 * Adjuntar documentos a una carpeta (equivale a cji-df:otros de Alfresco).
 *
 * Dos caras en la misma pantalla:
 *  - Entregar: registro de folios que se dejan para anexar a una carpeta.
 *  - Recibidos: lo que a mí me toca anexar durante mi turno, y que acepto.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../servicios/api';
import { buscarExpedientes, otrosPendientesDe } from '../servicios/consultas';
import { Encabezado, SelectUsuarios, alias } from '../componentes/Comunes';
import { aFecha, mensajeError } from '../util';
import type { Expediente, OtroDocumento } from '@cji/shared';

export function AdjuntarDocumentos() {
  const { usuario } = useAuth();
  const [aviso, setAviso] = useState<{ t: 'ok' | 'error'; m: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // --- Entregar ---
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<Expediente[]>([]);
  const [elegido, setElegido] = useState<Expediente | null>(null);
  const [folios, setFolios] = useState('1');
  const [descripcion, setDescripcion] = useState('');
  const [formulario, setFormulario] = useState('');
  const [asignado, setAsignado] = useState('');

  // --- Recibidos ---
  const [pendientes, setPendientes] = useState<OtroDocumento[]>([]);

  useEffect(() => { void recargar(); }, [usuario?.email]);

  async function recargar() {
    if (!usuario?.email) return;
    try { setPendientes(await otrosPendientesDe(usuario.email)); } catch { /* sin permisos aún */ }
  }

  async function buscar(e: React.FormEvent) {
    e.preventDefault();
    if (!texto.trim()) return;
    setOcupado(true);
    try { setResultados(await buscarExpedientes(texto.trim().toUpperCase())); }
    catch (err) { setAviso({ t: 'error', m: mensajeError(err) }); }
    finally { setOcupado(false); }
  }

  async function entregar(e: React.FormEvent) {
    e.preventDefault();
    if (!elegido) return;
    setOcupado(true);
    try {
      const r = await api.crearOtroDocumento({
        expedienteId: elegido.id,
        folios: Number(folios),
        descripcion: descripcion.trim(),
        asignadoEmail: asignado,
        formulario: formulario.trim() || undefined,
      });
      setAviso({ t: 'ok', m: `Registrado ${r.radicadoOtro}. Queda pendiente de anexar por ${alias(asignado)}.` });
      setElegido(null); setDescripcion(''); setFolios('1'); setFormulario('');
      setResultados([]); setTexto('');
      void recargar();
    } catch (err) { setAviso({ t: 'error', m: mensajeError(err) }); }
    finally { setOcupado(false); }
  }

  async function aceptar(o: OtroDocumento) {
    setOcupado(true);
    try {
      await api.aceptarOtroDocumento({ expedienteId: o.expedienteId, otroId: o.id });
      setAviso({ t: 'ok', m: 'Documento anexado.' });
      void recargar();
    } catch (err) { setAviso({ t: 'error', m: mensajeError(err) }); }
    finally { setOcupado(false); }
  }

  return (
    <>
      <Encabezado icono="bx-paperclip" titulo="Adjuntar Documentos" />

      {aviso && <div className={`aviso ${aviso.t}`}>{aviso.m}</div>}

      <div className="tarjeta">
        <h3>Entregar documentos para anexar</h3>

        {!elegido ? (
          <form onSubmit={buscar} className="fila" style={{ alignItems: 'flex-end' }}>
            <div className="campo" style={{ flex: 1, marginBottom: 0, minWidth: 220 }}>
              <label>Radicado de la carpeta</label>
              <input value={texto} onChange={(e) => setTexto(e.target.value)}
                placeholder="Ej. A1007" autoCapitalize="characters" />
            </div>
            <button className="btn btn-primario" disabled={ocupado}>Buscar</button>
          </form>
        ) : (
          <form onSubmit={entregar} className="grid-2">
            <div className="campo">
              <label>Carpeta</label>
              <div className="fila">
                <b>{elegido.radicado}</b>
                <button type="button" className="btn btn-secundario" onClick={() => setElegido(null)}>Cambiar</button>
              </div>
            </div>
            <div className="campo">
              <label>Folios</label>
              <input type="number" min={1} max={9999} value={folios}
                onChange={(e) => setFolios(e.target.value)} required />
            </div>
            <div className="campo">
              <label>Descripción</label>
              <input value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Poder, Auto, Ruta, Respuesta…" required />
            </div>
            <div className="campo">
              <label>N.º de formulario <small>(opcional)</small></label>
              <input value={formulario} onChange={(e) => setFormulario(e.target.value)} placeholder="27498" />
            </div>
            <div className="campo">
              <label>Se los entrego a</label>
              <SelectUsuarios valor={asignado} onChange={setAsignado} />
            </div>
            <div className="campo">
              <button className="btn btn-primario" disabled={ocupado || !asignado}>Registrar entrega</button>
            </div>
          </form>
        )}

        {resultados.length > 0 && !elegido && (
          <table className="table_av">
            <thead><tr><th>Radicado</th><th>Título</th><th>Estado</th><th /></tr></thead>
            <tbody>
              {resultados.map((x) => (
                <tr key={x.id}>
                  <td><b>{x.radicado}</b></td>
                  <td>{x.titulo}</td>
                  <td>{x.estado}</td>
                  <td><button className="btn btn-secundario" onClick={() => setElegido(x)}>Elegir</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="tarjeta">
        <h3>Documentos que debo anexar ({pendientes.length})</h3>
        {pendientes.length === 0 ? (
          <p className="vacio">No tiene documentos pendientes de anexar.</p>
        ) : (
          <table className="table_av">
            <thead>
              <tr><th>Carpeta</th><th>Descripción</th><th>Folios</th><th>Entregado por</th><th>Fecha</th><th /></tr>
            </thead>
            <tbody>
              {pendientes.map((o) => (
                <tr key={o.id}>
                  <td><b>{o.radicado ?? o.expedienteId}</b></td>
                  <td>{o.descripcion}</td>
                  <td>{o.folios}</td>
                  <td>{alias(o.solicitanteEmail ?? '')}</td>
                  <td>{aFecha(o.creadoEn)?.toLocaleDateString('es-CO') ?? '—'}</td>
                  <td>
                    <button className="btn btn-primario" disabled={ocupado} onClick={() => void aceptar(o)}>
                      Anexar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
